/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { cn } from '@shared/lib/utils';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Whether to use compact styling (smaller text, tighter spacing) */
  compact?: boolean;
}

export function MarkdownRenderer({ content, className, compact = false }: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        "prose prose-slate dark:prose-invert max-w-none",
        compact ? "prose-sm" : "",
        // Customize colors to work better with the chat interface
        "prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground",
        "prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground",
        "prose-blockquote:text-muted-foreground prose-blockquote:border-l-border",
        "prose-hr:border-border",
        // Links styling
        "prose-a:text-primary hover:prose-a:text-primary/80 prose-a:no-underline hover:prose-a:underline",
        // List styling
        "prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground",
        // Table styling
        "prose-table:text-foreground prose-thead:text-foreground prose-tbody:text-foreground",
        "prose-th:border-border prose-td:border-border",
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Customize code blocks to have better styling
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match;
            return isInline ? (
              <code
                className={cn("rounded bg-muted px-1.5 py-0.5 text-sm", className)}
                {...props}
              >
                {children}
              </code>
            ) : (
              <pre className={cn("overflow-auto rounded-md bg-muted p-4")}>
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            );
          },
          // Customize links to open in new tab
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline"
              {...props}
            >
              {children}
            </a>
          ),
          // Customize blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote
              className="border-l-4 border-border bg-muted/50 pl-4 py-2 my-4 italic"
              {...props}
            >
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
